
import React, { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import Logo from "./navbar/Logo";
import NavLinks from "./navbar/NavLinks";
import { AuthButtons } from "./navbar/AuthButtons";
import MobileMenuButton from "./navbar/MobileMenuButton";
import MobileMenu from "./navbar/MobileMenu";
import { GuestSavesIndicator } from "./navbar/GuestSavesIndicator";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // If menu is open and click is outside menu and not on menu button
      if (isOpen && !target.closest('[data-mobile-menu]') && !target.closest('[data-mobile-button]')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <nav
        className={cn(
          "fixed top-0 w-full z-50 transition-all duration-300 border-b border-gray-100",
          scrolled
            ? "bg-white py-3 shadow-sm bg-white/95"
            : "bg-white py-4"
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <Logo />

              {/* Desktop Navigation */}
              <div className="hidden lg:block ml-6">
                <NavLinks 
                  locationDropdownOpen={locationDropdownOpen}
                  setLocationDropdownOpen={setLocationDropdownOpen}
                  guidesDropdownOpen={guidesDropdownOpen}
                  setGuidesDropdownOpen={setGuidesDropdownOpen}
                  resourcesDropdownOpen={resourcesDropdownOpen}
                  setResourcesDropdownOpen={setResourcesDropdownOpen}
                />
              </div>
            </div>

            {/* Guest Saves, Auth buttons and Waitlist CTA - Desktop */}
            <div className="hidden lg:flex items-center space-x-4">
              <GuestSavesIndicator key="navbar-saves-indicator" />
              <AuthButtons />
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden" data-mobile-button>
              <MobileMenuButton isOpen={isOpen} onClick={toggleMenu} />
            </div>
          </div>
        </div>

        {/* Dropdown overlay effect - shown when any dropdown is open */}
        {(locationDropdownOpen || guidesDropdownOpen || resourcesDropdownOpen) && (
          <div 
            className="fixed inset-0 bg-black/5 -z-10" 
            onClick={() => {
              setLocationDropdownOpen(false);
              setGuidesDropdownOpen(false);
              setResourcesDropdownOpen(false);
            }}
          />
        )}
      </nav>

      {/* Mobile menu */}
      <div data-mobile-menu>
        <MobileMenu isOpen={isOpen} onClose={() => setIsOpen(false)} />
      </div>
      
      <div className={cn("h-16 w-full", scrolled ? "h-[72px]" : "h-[80px]")}></div>
    </>
  );
}
